import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Image as ImageIcon, TextT, FloppyDisk, Eye, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { FONTS, COLOR_PRESETS, DEFAULT_FONT, DEFAULT_COLOR, fontCss, BODY_FONTS, DEFAULT_BODY_TYPO, bodyFontCss } from "@/lib/briefkopfStyles";

// Data model:
// briefkopf = { logo_size: "small"|"medium"|"large",
//   left:   { type: "empty"|"text"|"logo", content: "..." },
//   center: { type, content },
//   right:  { type, content } }
// fusszeile = { left, center, right }  (no logo for footer — type: empty|text)

const COLS = [
  { key: "left", label: "Links" },
  { key: "center", label: "Mitte" },
  { key: "right", label: "Rechts" },
];

const LOGO_SIZES = [
  { value: "small", label: "Klein", height: "14mm" },
  { value: "medium", label: "Mittel", height: "22mm" },
  { value: "large", label: "Groß", height: "32mm" },
];

const EMPTY_CELL = { type: "empty", content: "" };

const DEFAULT_BRIEFKOPF = {
  logo_size: "medium",
  left: EMPTY_CELL,
  center: EMPTY_CELL,
  right: EMPTY_CELL,
};

const DEFAULT_FUSSZEILE = {
  left: EMPTY_CELL,
  center: EMPTY_CELL,
  right: EMPTY_CELL,
};

const DEFAULT_SEITENKOPF = {
  width: 28,
  blocks: [
    { type: "empty", content: "" },
    { type: "empty", content: "" },
    { type: "empty", content: "" },
  ],
};

// Migrate legacy seitenkopf shape ({ type, content }) to 3-block array
function migrateSeitenkopf(sk) {
  if (!sk) return DEFAULT_SEITENKOPF;
  if (Array.isArray(sk.blocks)) {
    const arr = [...sk.blocks];
    while (arr.length < 3) arr.push({ type: "empty", content: "" });
    return { width: sk.width ?? 28, blocks: arr.slice(0, 3) };
  }
  // legacy single-cell shape
  if (sk.type) {
    return {
      width: 28,
      blocks: [
        { ...sk },
        { type: "empty", content: "" },
        { type: "empty", content: "" },
      ],
    };
  }
  return DEFAULT_SEITENKOPF;
}

// Migrate old briefkopf shape ({ logo_position, blocks: [...] }) to new shape
function migrate(briefkopf) {
  if (!briefkopf) return DEFAULT_BRIEFKOPF;
  if (briefkopf.left || briefkopf.center || briefkopf.right) {
    return { ...DEFAULT_BRIEFKOPF, ...briefkopf };
  }
  // legacy
  const next = { ...DEFAULT_BRIEFKOPF, logo_size: briefkopf.logo_size || "medium" };
  if (briefkopf.logo_position && ["left", "center", "right"].includes(briefkopf.logo_position)) {
    next[briefkopf.logo_position] = { type: "logo", content: "" };
  }
  for (const b of briefkopf.blocks || []) {
    if (!["left", "center", "right"].includes(b.position)) continue;
    if (next[b.position].type === "empty") {
      next[b.position] = { type: "text", content: b.content || "" };
    }
  }
  return next;
}

function migrateFusszeile(fz) {
  if (!fz) return DEFAULT_FUSSZEILE;
  if (Array.isArray(fz)) {
    const next = { ...DEFAULT_FUSSZEILE };
    for (const b of fz) {
      if (!["left", "center", "right"].includes(b.position)) continue;
      if (next[b.position].type === "empty") {
        next[b.position] = { type: "text", content: b.content || "" };
      }
    }
    return next;
  }
  return { ...DEFAULT_FUSSZEILE, ...fz };
}

const DEFAULT_LINIEN = {
  briefkopf_unten: true,
  fusszeile_oben: true,
  seitenkopf_links: true,
  seitenkopf_blocks: true,
};

const BODY_PLACEHOLDER_TITLE = "Verkehrsunfall vom 15.04.2026 — Mandantin: Erika Mustermann";

const PLACEHOLDER_BODY = `Sehr geehrte Damen und Herren,

in vorbezeichneter Angelegenheit zeigen wir an, dass uns Frau Erika Mustermann mit der Wahrnehmung ihrer rechtlichen Interessen beauftragt hat. Eine ordnungsgemäße Vollmacht liegt vor und wird auf Verlangen jederzeit nachgereicht.

Unsere Mandantin wurde am 15. April 2026 gegen 14:30 Uhr in Köln, Hohenzollernring 12, durch einen Verkehrsunfall geschädigt, den der bei Ihnen versicherte Fahrer des Fahrzeugs mit dem amtlichen Kennzeichen K-AB 1234 allein und schuldhaft verursacht hat.

Wir bitten daher um Anerkennung der vollen Haftung dem Grunde nach sowie um Regulierung der nachstehend bezifferten Schadensersatzansprüche binnen 14 Tagen.

Mit freundlichen Grüßen`;

export default function BriefkopfDesigner({ user, setUser }) {
  const [briefkopf, setBriefkopf] = useState(() => migrate(user?.briefkopf));
  const [fusszeile, setFusszeile] = useState(() => migrateFusszeile(user?.fusszeile));
  const [seitenkopf, setSeitenkopf] = useState(() => migrateSeitenkopf(user?.seitenkopf));
  const [linien, setLinien] = useState(() => ({ ...DEFAULT_LINIEN, ...(user?.linien || {}) }));
  const [bodyTypo, setBodyTypo] = useState(() => ({ ...DEFAULT_BODY_TYPO, ...(user?.body_typografie || {}) }));
  const [logoUrl, setLogoUrl] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true; let url;
    (async () => {
      if (!user?.logo_path) { setLogoUrl(null); return; }
      try {
        const r = await api.get("/auth/logo", { responseType: "blob" });
        if (!active) return;
        url = URL.createObjectURL(r.data);
        setLogoUrl(url);
      } catch { setLogoUrl(null); }
    })();
    return () => { active = false; if (url) URL.revokeObjectURL(url); };
  }, [user?.logo_path]);

  const setHeaderCol = (col, patch) =>
    setBriefkopf(b => ({ ...b, [col]: { ...b[col], ...patch } }));

  const setFooterCol = (col, patch) =>
    setFusszeile(f => ({ ...f, [col]: { ...f[col], ...patch } }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch("/auth/me", { briefkopf, fusszeile, seitenkopf, linien, body_typografie: bodyTypo });
      localStorage.setItem("kvr_user", JSON.stringify(r.data));
      setUser(r.data);
      toast.success("Briefkopf & Fußzeile gespeichert");
    } catch {
      toast.error("Fehler beim Speichern");
    } finally { setSaving(false); }
  };

  const fillDefault = () => {
    const headerLeft = [
      user?.kanzlei_name,
      user?.kanzlei_adresse,
      [user?.kanzlei_plz, user?.kanzlei_ort].filter(Boolean).join(" "),
      user?.kanzlei_telefon && `Tel.: ${user.kanzlei_telefon}`,
      user?.kanzlei_email,
      user?.kanzlei_website,
    ].filter(Boolean).join("\n");
    setBriefkopf({
      logo_size: "medium",
      left:   { type: "text", content: headerLeft },
      center: EMPTY_CELL,
      right:  { type: "logo", content: "" },
    });
    const footerLeft = [
      user?.kanzlei_bank,
      user?.kanzlei_iban && `IBAN: ${user.kanzlei_iban}`,
      user?.kanzlei_bic && `BIC: ${user.kanzlei_bic}`,
    ].filter(Boolean).join("\n");
    const footerCenter = user?.kanzlei_anderkonto_iban
      ? ["Anderkonto:", `IBAN: ${user.kanzlei_anderkonto_iban}`, user?.kanzlei_anderkonto_bic && `BIC: ${user.kanzlei_anderkonto_bic}`].filter(Boolean).join("\n")
      : "";
    const footerRight = [
      user?.kanzlei_kammer && !/^https?:/i.test(user.kanzlei_kammer) ? `Mitglied der ${user.kanzlei_kammer}` : null,
      user?.kanzlei_ustid && `USt-IdNr.: ${user.kanzlei_ustid}`,
      user?.kanzlei_gerichtsstand && `Gerichtsstand: ${user.kanzlei_gerichtsstand}`,
    ].filter(Boolean).join("\n");
    setFusszeile({
      left:   footerLeft   ? { type: "text", content: footerLeft }   : EMPTY_CELL,
      center: footerCenter ? { type: "text", content: footerCenter } : EMPTY_CELL,
      right:  footerRight  ? { type: "text", content: footerRight }  : EMPTY_CELL,
    });
    const sideBlock1Logo = { type: "logo", content: "" };
    const sideBlock2 = [user?.full_name, user?.anwalt_titel].filter(Boolean).join("\n");
    const sideBlock3 = [
      user?.kanzlei_telefon && `Tel ${user.kanzlei_telefon}`,
      user?.kanzlei_fax && `Fax ${user.kanzlei_fax}`,
      user?.kanzlei_email,
      user?.kanzlei_website,
    ].filter(Boolean).join("\n");
    setSeitenkopf({
      blocks: [
        sideBlock1Logo,
        sideBlock2 ? { type: "text", content: sideBlock2 } : { type: "empty", content: "" },
        sideBlock3 ? { type: "text", content: sideBlock3 } : { type: "empty", content: "" },
      ],
    });
    toast.success("Vorschlag aus Kanzleidaten übernommen — bitte speichern");
  };

  return (
    <div className="space-y-6" data-testid="briefkopf-designer">
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="font-serif text-2xl">Briefkopf & Fußzeile gestalten</h3>
            <p className="text-sm text-[#4B5563] mt-1">
              Drei Spalten (Links · Mitte · Rechts). Pro Spalte entweder das Kanzlei-Logo oder einen Textblock.
            </p>
          </div>
          <Button variant="outline" onClick={fillDefault} data-testid="briefkopf-default-btn">
            Aus Kanzleidaten vorbefüllen
          </Button>
        </div>
      </div>

      {/* Briefkopf Editor */}
      <SectionEditor
        kicker="Briefkopf"
        title="3 Spalten — Logo oder Text"
        cols={briefkopf}
        setCol={setHeaderCol}
        allowLogo
        logoUrl={logoUrl}
        testidPrefix="header"
      />

      {/* Live Preview + Seitenkopf Editor side-by-side */}
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-6">
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-6 min-w-0">
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Eye size={20} weight="duotone" className="text-[#1A2238]" />
              <h4 className="font-serif text-xl">Live-Vorschau</h4>
            </div>
            <LinienToggles linien={linien} setLinien={setLinien} />
          </div>
          <BodyTypoBar bodyTypo={bodyTypo} setBodyTypo={setBodyTypo} />
          <SheetPreview briefkopf={briefkopf} fusszeile={fusszeile} seitenkopf={seitenkopf} linien={linien} bodyTypo={bodyTypo} logoUrl={logoUrl} />
        </div>

        <div className="bg-white border border-[#E5E7EB] rounded-lg p-6 space-y-4 self-start" data-testid="seitenkopf-editor">
          <div>
            <p className="overline text-[#D32F2F] mb-2">Seitenkopf</p>
            <h4 className="font-serif text-xl">Rechte Seitenleiste</h4>
            <p className="text-sm text-[#4B5563] mt-1">
              Drei Blöcke am rechten Seitenrand. Block 1 kann Logo oder Text sein, Block 2 & 3 nur Text.
              Die Breite gilt für alle Blöcke und schiebt den Schriftsatz-Body entsprechend ein.
            </p>
          </div>
          <div className="border border-[#E5E7EB] rounded-lg p-3 bg-[#F9FAFB] space-y-2">
            <SliderControl
              label="Breite"
              value={seitenkopf.width ?? 28}
              min={15} max={60} unit="mm"
              onChange={(v) => setSeitenkopf(s => ({ ...s, width: v }))}
              testid="seitenkopf-width"
            />
            <SliderControl
              label="↑ ↓"
              value={seitenkopf.offset_y ?? 0}
              min={-15} max={80} unit="mm"
              onChange={(v) => setSeitenkopf(s => ({ ...s, offset_y: v }))}
              testid="seitenkopf-offset-y"
            />
          </div>
          <div className="space-y-3">
            {(seitenkopf.blocks || []).map((b, idx) => (
              <CellEditor
                key={idx}
                label={`Block ${idx + 1}`}
                cell={b}
                onChange={(patch) => setSeitenkopf(s => {
                  const blocks = [...s.blocks];
                  blocks[idx] = { ...blocks[idx], ...patch };
                  return { ...s, blocks };
                })}
                allowLogo={idx === 0}
                logoUrl={logoUrl}
                testid={`seitenkopf-block-${idx}`}
                compact
                hideLogoWidth
              />
            ))}
          </div>
        </div>
      </div>

      {/* Fußzeile Editor */}
      <SectionEditor
        kicker="Fußzeile"
        title="3 Spalten — Text"
        cols={fusszeile}
        setCol={setFooterCol}
        allowLogo={false}
        testidPrefix="footer"
      />

      <div className="flex justify-end gap-2 pt-2 pb-2">
        <Button onClick={save} disabled={saving} className="bg-[#1A2238] hover:bg-[#2C3859]" data-testid="briefkopf-save-btn">
          <FloppyDisk size={16} className="mr-2" /> {saving ? "Speichert..." : "Briefkopf & Fußzeile speichern"}
        </Button>
      </div>
    </div>
  );
}

function SectionEditor({ kicker, title, cols, setCol, allowLogo, logoUrl, testidPrefix }) {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg p-6 space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="overline text-[#D32F2F] mb-2">{kicker}</p>
          <h4 className="font-serif text-xl">{title}</h4>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {COLS.map(({ key, label }) => (
          <CellEditor
            key={key}
            label={label}
            cell={cols[key]}
            onChange={(patch) => setCol(key, patch)}
            allowLogo={allowLogo}
            logoUrl={logoUrl}
            testid={`${testidPrefix}-${key}`}
          />
        ))}
      </div>
    </div>
  );
}

function CellEditor({ label, cell, onChange, allowLogo, logoUrl, testid, compact = false, hideLogoWidth = false }) {
  const type = cell?.type || "empty";
  const font = cell?.font_family || DEFAULT_FONT;
  const color = cell?.font_color || DEFAULT_COLOR;
  return (
    <div className="border border-[#E5E7EB] rounded-lg p-4 space-y-3" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-[#0A192F] uppercase tracking-wide">{label}</span>
        {type !== "empty" && (
          <button
            type="button"
            onClick={() => onChange({ type: "empty", content: "" })}
            className="text-[#9CA3AF] hover:text-[#D32F2F] transition-colors"
            title="Leeren"
            data-testid={`${testid}-clear`}
          >
            <X size={14} />
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        <button
          type="button"
          onClick={() => onChange({ type: "text", content: cell.content || "" })}
          className={`flex items-center justify-center gap-1.5 px-2 py-2 border rounded text-xs transition-all ${
            type === "text" ? "border-[#1A2238] bg-[#1A2238] text-white" : "border-[#E5E7EB] hover:border-[#1A2238]"
          }`}
          data-testid={`${testid}-type-text`}
        >
          <TextT size={14} weight="bold" /> Text
        </button>
        {allowLogo ? (
          <button
            type="button"
            onClick={() => onChange({ type: "logo", content: "", logo_height: cell.logo_height ?? 22, logo_offset_x: cell.logo_offset_x ?? 0, logo_offset_y: cell.logo_offset_y ?? 0 })}
            disabled={!logoUrl}
            className={`flex items-center justify-center gap-1.5 px-2 py-2 border rounded text-xs transition-all ${
              type === "logo" ? "border-[#1A2238] bg-[#1A2238] text-white" : "border-[#E5E7EB] hover:border-[#1A2238]"
            } ${!logoUrl ? "opacity-50 cursor-not-allowed" : ""}`}
            title={!logoUrl ? "Bitte zuerst Logo unter Kanzleidaten hochladen" : ""}
            data-testid={`${testid}-type-logo`}
          >
            <ImageIcon size={14} weight="duotone" /> Logo
          </button>
        ) : (
          <button
            type="button"
            disabled
            className="flex items-center justify-center gap-1.5 px-2 py-2 border border-[#E5E7EB] rounded text-xs opacity-40 cursor-not-allowed"
          >
            <ImageIcon size={14} /> Logo
          </button>
        )}
      </div>
      {type === "text" && (
        <>
          <Textarea
            rows={compact ? 3 : 4}
            value={cell.content || ""}
            onChange={e => onChange({ content: e.target.value })}
            placeholder={"z.B.\nKanzlei Müller\nMusterstr. 1\n10115 Berlin"}
            className="text-sm"
            style={{ fontFamily: fontCss(font), color }}
            data-testid={`${testid}-content`}
          />
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[10px] uppercase tracking-wide text-[#6B7280] mb-1">Schriftart</label>
              <select
                value={font}
                onChange={e => onChange({ font_family: e.target.value })}
                className="w-full text-xs border border-[#E5E7EB] rounded px-2 py-1.5 bg-white focus:outline-none focus:border-[#1A2238]"
                data-testid={`${testid}-font`}
              >
                {FONTS.map(f => (
                  <option key={f.value} value={f.value} style={{ fontFamily: f.css }}>{f.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wide text-[#6B7280] mb-1">Farbe</label>
              <div className="flex items-center gap-1.5">
                <input
                  type="color"
                  value={color}
                  onChange={e => onChange({ font_color: e.target.value })}
                  className="w-8 h-8 rounded border border-[#E5E7EB] cursor-pointer p-0.5"
                  data-testid={`${testid}-color`}
                  title="Farbe wählen"
                />
                <div className="flex flex-wrap gap-1">
                  {COLOR_PRESETS.map(c => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => onChange({ font_color: c })}
                      className={`w-5 h-5 rounded border transition-all ${color === c ? "ring-2 ring-offset-1 ring-[#1A2238] border-transparent" : "border-[#E5E7EB] hover:scale-110"}`}
                      style={{ backgroundColor: c }}
                      data-testid={`${testid}-color-preset-${c.replace("#","")}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      {type === "logo" && (
        <>
          <div className="border border-dashed border-[#E5E7EB] rounded p-3 bg-[#F9FAFB] min-h-[80px] flex items-start justify-start overflow-auto">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt="Logo"
                style={{
                  height: `${cell.logo_height ?? 22}mm`,
                  width: cell.logo_width ? `${cell.logo_width}mm` : "auto",
                  marginLeft: `${cell.logo_offset_x ?? 0}mm`,
                  marginTop: `${cell.logo_offset_y ?? 0}mm`,
                  maxWidth: "none",
                  objectFit: "contain",
                  flexShrink: 0,
                }}
              />
            ) : (
              <span className="text-xs text-[#9CA3AF] italic">Kein Logo verfügbar — unter „Kanzleidaten" hochladen</span>
            )}
          </div>
          {logoUrl && (
            <div className="space-y-2 pt-1">
              <SliderControl
                label="Höhe"
                value={cell.logo_height ?? 22}
                min={8} max={60} unit="mm"
                onChange={(v) => onChange({ logo_height: v })}
                testid={`${testid}-logo-height`}
              />
              {!hideLogoWidth && (
                <SliderControl
                  label="Breite"
                  value={cell.logo_width ?? 0}
                  min={0} max={150} unit="mm"
                  onChange={(v) => onChange({ logo_width: v })}
                  testid={`${testid}-logo-width`}
                  autoLabel="auto"
                />
              )}
              <SliderControl
                label="← →"
                value={cell.logo_offset_x ?? 0}
                min={-20} max={20} unit="mm"
                onChange={(v) => onChange({ logo_offset_x: v })}
                testid={`${testid}-logo-x`}
              />
              <SliderControl
                label="↑ ↓"
                value={cell.logo_offset_y ?? 0}
                min={-15} max={20} unit="mm"
                onChange={(v) => onChange({ logo_offset_y: v })}
                testid={`${testid}-logo-y`}
              />
            </div>
          )}
        </>
      )}
      {type === "empty" && (
        <div className="border border-dashed border-[#E5E7EB] rounded p-3 text-center text-xs text-[#9CA3AF] italic min-h-[60px] flex items-center justify-center">
          Leer — wählen Sie Text oder Logo
        </div>
      )}
    </div>
  );
}

function SheetPreview({ briefkopf, fusszeile, seitenkopf, linien, bodyTypo, logoUrl }) {
  const L = { ...DEFAULT_LINIEN, ...(linien || {}) };
  const T = { ...DEFAULT_BODY_TYPO, ...(bodyTypo || {}) };
  const LEGACY_LOGO_MM = { small: 14, medium: 22, large: 32 };
  const fallbackHeaderH = LEGACY_LOGO_MM[briefkopf?.logo_size] ?? 22;
  const sideBlocks = (seitenkopf?.blocks || []).filter(b =>
    (b.type === "text" && (b.content || "").trim()) || (b.type === "logo")
  );
  const hasSide = sideBlocks.length > 0;
  const sideW = seitenkopf?.width ?? 28;
  // Body right padding = sidebar width + 6mm gutter + outer 10mm right margin
  const bodyRight = hasSide ? `${sideW + 16}mm` : "18mm";

  const logoStyle = (cell, fallbackH) => ({
    height: `${cell.logo_height ?? fallbackH}mm`,
    width: cell.logo_width ? `${cell.logo_width}mm` : "auto",
    marginLeft: `${cell.logo_offset_x ?? 0}mm`,
    marginTop: `${cell.logo_offset_y ?? 0}mm`,
    maxWidth: "none",
    objectFit: "contain",
    flexShrink: 0,
  });

  const textStyle = (cell, base = {}) => ({
    ...base,
    fontFamily: fontCss(cell?.font_family || DEFAULT_FONT),
    color: cell?.font_color || base.color || DEFAULT_COLOR,
  });

  const renderHeaderCol = (cell, align) => {
    const flexAlign = align === "center" ? "center" : align === "right" ? "flex-end" : "flex-start";
    if (cell?.type === "logo" && logoUrl) {
      return (
        <div style={{ display: "flex", justifyContent: flexAlign }}>
          <img src={logoUrl} alt="Logo" style={logoStyle(cell, fallbackHeaderH)} />
        </div>
      );
    }
    if (cell?.type === "text" && cell.content) {
      return (
        <div style={textStyle(cell, { textAlign: align, fontSize: "8pt", whiteSpace: "pre-line", lineHeight: 1.4 })}>
          {cell.content}
        </div>
      );
    }
    return null;
  };

  const renderFooterCol = (cell, align) => {
    if (cell?.type === "text" && cell.content) {
      return (
        <div style={textStyle(cell, { textAlign: align, fontSize: "7pt", color: cell?.font_color || "#6B7280", whiteSpace: "pre-line", lineHeight: 1.5 })}>
          {cell.content}
        </div>
      );
    }
    return null;
  };

  const renderSideBlock = (b, i) => {
    if (b.type === "logo" && logoUrl) {
      return (
        <div key={i} style={{ marginBottom: "3mm", overflow: "hidden" }}>
          <img src={logoUrl} alt="Logo" style={{
            height: `${b.logo_height ?? 16}mm`,
            width: b.logo_width ? `${b.logo_width}mm` : "auto",
            marginLeft: `${b.logo_offset_x ?? 0}mm`,
            marginTop: `${b.logo_offset_y ?? 0}mm`,
            maxWidth: "100%",
            objectFit: "contain",
          }} />
        </div>
      );
    }
    if (b.type === "text" && (b.content || "").trim()) {
      return (
        <div key={i} style={textStyle(b, {
          fontSize: "6.5pt",
          lineHeight: 1.5,
          whiteSpace: "pre-line",
          color: b?.font_color || "#4B5563",
          paddingTop: i > 0 ? "2.5mm" : 0,
          marginTop: i > 0 ? "2.5mm" : 0,
          borderTop: (i > 0 && L.seitenkopf_blocks) ? "1px solid #E5E7EB" : "none",
        })}>
          {b.content}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-[#E5E7EB] rounded p-4 overflow-auto">
      <div
        className="bg-white mx-auto shadow-sm"
        style={{
          width: "210mm",
          minHeight: "297mm",
          padding: `20mm ${bodyRight} 20mm 18mm`,
          fontFamily: "'IBM Plex Sans', sans-serif",
          color: "#0A192F",
          position: "relative",
          transform: "scale(0.55)",
          transformOrigin: "top center",
          marginBottom: "-130mm",
        }}
      >
        {/* SEITENKOPF (vertical right column with up to 3 stacked blocks) */}
        {hasSide && (
          <div style={{
            position: "absolute",
            top: `${20 + (seitenkopf?.offset_y ?? 0)}mm`,
            right: "10mm",
            width: `${sideW}mm`,
            paddingLeft: L.seitenkopf_links ? "3mm" : 0,
            borderLeft: L.seitenkopf_links ? "1px solid #1A2238" : "none",
          }}>
            {(seitenkopf.blocks || []).map((b, i) => renderSideBlock(b, i))}
          </div>
        )}

        {/* HEADER ROW */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: "6mm",
          borderBottom: L.briefkopf_unten ? "1px solid #111" : "none",
          paddingBottom: L.briefkopf_unten ? "5mm" : 0,
          marginBottom: "6mm",
          alignItems: "flex-start",
        }}>
          {renderHeaderCol(briefkopf.left, "left")}
          {renderHeaderCol(briefkopf.center, "center")}
          {renderHeaderCol(briefkopf.right, "right")}
        </div>

        {/* PLACEHOLDER BODY */}
        <div style={{
          marginTop: "8mm",
          fontFamily: bodyFontCss(T.font_family),
          fontSize: `${T.font_size}pt`,
          lineHeight: T.line_height,
          color: "#0A192F",
          textAlign: "justify",
          hyphens: "auto",
        }}>
          <div style={{ fontWeight: 600, marginBottom: "4mm", fontSize: `${T.font_size + 1}pt` }}>
            {BODY_PLACEHOLDER_TITLE}
          </div>
          {PLACEHOLDER_BODY.split("\n\n").map((para, i) => (
            <p key={i} style={{ margin: "0 0 3mm 0" }}>{para}</p>
          ))}
        </div>

        {/* FOOTER */}
        <div style={{
          position: "absolute",
          bottom: "12mm",
          left: "18mm",
          right: bodyRight,
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: "6mm",
          paddingTop: L.fusszeile_oben ? "4mm" : 0,
          borderTop: L.fusszeile_oben ? "1px solid #E5E7EB" : "none",
        }}>
          {renderFooterCol(fusszeile.left, "left")}
          {renderFooterCol(fusszeile.center, "center")}
          {renderFooterCol(fusszeile.right, "right")}
        </div>
      </div>
    </div>
  );
}


function SliderControl({ label, value, min, max, step = 1, unit = "", onChange, testid, autoLabel }) {
  const display = (autoLabel && Number(value) === 0) ? autoLabel : `${value}${unit}`;
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] font-medium text-[#6B7280] uppercase tracking-wide w-10 shrink-0">{label}</span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="flex-1 accent-[#1A2238] cursor-pointer"
        data-testid={testid}
      />
      <span className="text-[11px] font-mono text-[#0A192F] w-12 text-right tabular-nums">
        {display}
      </span>
    </div>
  );
}


function LinienToggles({ linien, setLinien }) {
  const items = [
    { key: "briefkopf_unten",    label: "Briefkopf-Linie" },
    { key: "fusszeile_oben",     label: "Fußzeilen-Linie" },
    { key: "seitenkopf_links",   label: "Seitenkopf-Linie" },
    { key: "seitenkopf_blocks",  label: "Block-Trenner" },
  ];
  const toggle = (k) => setLinien(l => ({ ...l, [k]: !l[k] }));
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="linien-toggles">
      <span className="text-[10px] uppercase tracking-wide text-[#6B7280] mr-1">Trennlinien:</span>
      {items.map(({ key, label }) => {
        const on = !!linien?.[key];
        return (
          <button
            key={key}
            type="button"
            onClick={() => toggle(key)}
            className={`px-2 py-1 rounded text-[11px] border transition-all ${
              on
                ? "bg-[#1A2238] text-white border-[#1A2238]"
                : "bg-white text-[#6B7280] border-[#E5E7EB] hover:border-[#1A2238]"
            }`}
            data-testid={`linien-${key}`}
            title={on ? "Linie sichtbar — klicken zum Ausblenden" : "Linie ausgeblendet — klicken zum Anzeigen"}
          >
            {on ? "✓" : "○"} {label}
          </button>
        );
      })}
    </div>
  );
}


function BodyTypoBar({ bodyTypo, setBodyTypo }) {
  const T = { ...DEFAULT_BODY_TYPO, ...(bodyTypo || {}) };
  const set = (patch) => setBodyTypo(b => ({ ...b, ...patch }));
  return (
    <div className="flex flex-wrap items-center gap-3 mb-4 px-3 py-2 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg" data-testid="body-typo-bar">
      <span className="text-[10px] uppercase tracking-wide text-[#6B7280] font-medium">Schriftsatz-Body:</span>
      <div className="flex items-center gap-1.5">
        <label className="text-[10px] text-[#6B7280]">Schriftart</label>
        <select
          value={T.font_family}
          onChange={e => set({ font_family: e.target.value })}
          className="text-xs border border-[#E5E7EB] rounded px-2 py-1 bg-white focus:outline-none focus:border-[#1A2238]"
          style={{ fontFamily: bodyFontCss(T.font_family) }}
          data-testid="body-font-family"
        >
          {BODY_FONTS.map(f => (
            <option key={f.value} value={f.value} style={{ fontFamily: f.css }}>{f.label}</option>
          ))}
        </select>
      </div>
      <div className="flex items-center gap-1.5">
        <label className="text-[10px] text-[#6B7280]">Größe</label>
        <select
          value={T.font_size}
          onChange={e => set({ font_size: Number(e.target.value) })}
          className="text-xs border border-[#E5E7EB] rounded px-2 py-1 bg-white focus:outline-none focus:border-[#1A2238] tabular-nums"
          data-testid="body-font-size"
        >
          {[8, 9, 10, 11, 12, 13, 14, 15, 16].map(s => (
            <option key={s} value={s}>{s} pt</option>
          ))}
        </select>
      </div>
      <div className="flex items-center gap-1.5">
        <label className="text-[10px] text-[#6B7280]">Zeilenabstand</label>
        <select
          value={T.line_height}
          onChange={e => set({ line_height: Number(e.target.value) })}
          className="text-xs border border-[#E5E7EB] rounded px-2 py-1 bg-white focus:outline-none focus:border-[#1A2238] tabular-nums"
          data-testid="body-line-height"
        >
          <option value={1.2}>Schmal (1,2)</option>
          <option value={1.4}>Normal (1,4)</option>
          <option value={1.5}>Etwas weit (1,5)</option>
          <option value={1.6}>Komfortabel (1,6)</option>
          <option value={1.8}>Weit (1,8)</option>
          <option value={2.0}>Doppelt (2,0)</option>
        </select>
      </div>
    </div>
  );
}
